/**
 * Local VDR indexing queue (upload tray + Ask Lens "X processing").
 *
 * Entries are tab-session only (sessionStorage). They must never live in
 * redux-persist/localStorage — that is what left "processing" badges stuck
 * for days after indexing had finished.
 */

export const INDEXING_STORAGE_KEY = 'vdr_indexing_files';
/** File still missing from both Azure indexes after this age → failed. */
export const VDR_INDEXING_MAX_AGE_MS = 15 * 60 * 1000;
/** Keyword indexer done but RAG chunks still missing after this age → failed. */
export const VDR_RAG_CATCHUP_MAX_AGE_MS = 45 * 60 * 1000;
export const INDEXING_FAILED_MESSAGE = 'Indexing failed';

/**
 * Map an Azure index_status row to local UI state.
 *
 * The upload loader clears when the blob indexer has the file
 * (`keyword_indexed` / status `processing`). RAG chunks and AI-origin
 * analysis keep running after that and must not hold the loader.
 * `pending` means the file is in neither index yet.
 */
export const classifyAzureIndexStatus = (row, { startedAt, now = Date.now() } = {}) => {
    const status = String(row?.status || '')
        .toLowerCase()
        .trim();
    const ragReady = Boolean(row?.indexed) || status === 'indexed';
    const keywordReady = Boolean(row?.keyword_indexed) || status === 'processing';
    if (
        ragReady ||
        keywordReady ||
        status === 'not_applicable' ||
        status === 'not_found'
    ) {
        return 'done';
    }

    const started = Number(startedAt) || 0;
    const age = started > 0 ? now - started : 0;
    return age > VDR_INDEXING_MAX_AGE_MS ? 'failed' : 'indexing';
};

export const emptyUploadTray = () => ({
    items: [],
    collapsed: false,
    destinationLabel: '',
});

export const readIndexingStorage = () => {
    try {
        const raw = sessionStorage.getItem(INDEXING_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const writeIndexingStorage = entries => {
    try {
        sessionStorage.setItem(INDEXING_STORAGE_KEY, JSON.stringify(entries || []));
    } catch {
        // ignore quota / private mode failures
    }
};

export const normalizeIndexingEntry = (item, now = Date.now()) => {
    if (!item || typeof item !== 'object') return null;
    if (item.id == null || item.wsId == null || !item.projectId) return null;
    const startedAt = Number(item.startedAt);
    const status = item.status === 'failed' ? 'failed' : 'indexing';
    return {
        id: Number(item.id),
        wsId: Number(item.wsId),
        projectId: String(item.projectId),
        // Legacy entries without startedAt expire immediately so stuck
        // spinners cannot loop forever across refreshes.
        startedAt: Number.isFinite(startedAt) && startedAt > 0 ? startedAt : 0,
        status,
    };
};

const matchesProject = (item, projectId) => {
    if (projectId == null || projectId === '') return true;
    return item.projectId == null || String(item.projectId) === String(projectId);
};

export const pruneExpiredIndexingEntries = (entries, now = Date.now()) =>
    (entries || [])
        .map(item => normalizeIndexingEntry(item, now))
        .filter(Boolean)
        .flatMap(item => {
            if (item.status === 'failed') return [item];
            // Legacy rows without startedAt are dropped so stuck spinners cannot return.
            if (!(item.startedAt > 0)) return [];
            // Hard cap only — success/failure otherwise comes from Azure index presence.
            if (now - item.startedAt > VDR_RAG_CATCHUP_MAX_AGE_MS) {
                return [{ ...item, status: 'failed' }];
            }
            return [item];
        });

export const isIndexingEntryActive = (item, now = Date.now()) => {
    const normalized = normalizeIndexingEntry(item, now);
    if (!normalized || normalized.status === 'failed') return false;
    if (!(normalized.startedAt > 0)) return false;
    return now - normalized.startedAt <= VDR_RAG_CATCHUP_MAX_AGE_MS;
};

export const getActiveIndexingFiles = (indexingFiles, { projectId, now = Date.now() } = {}) => {
    return (indexingFiles || []).filter(item => {
        if (!isIndexingEntryActive(item, now)) return false;
        return matchesProject(item, projectId);
    });
};

export const getFailedIndexingFiles = (indexingFiles, { projectId } = {}) =>
    (indexingFiles || []).filter(item => {
        const normalized = normalizeIndexingEntry(item);
        if (!normalized || normalized.status !== 'failed') return false;
        return matchesProject(normalized, projectId);
    });

export const activeIndexingFileIds = (indexingFiles, options) =>
    getActiveIndexingFiles(indexingFiles, options)
        .map(item => Number(item.id))
        .filter(Number.isFinite);

export const failedIndexingFileIds = (indexingFiles, options) =>
    getFailedIndexingFiles(indexingFiles, options)
        .map(item => Number(item.id))
        .filter(Number.isFinite);

export const expireStaleTrayItems = (items, now = Date.now()) =>
    (items || []).map(item => {
        if (item?.status !== 'indexing') return item;
        // Missing startedAt is treated as already expired (stuck persist leftovers).
        const started = Number(item.startedAt) || 0;
        if (!started) {
            return { ...item, status: 'ready', error: undefined };
        }
        if (now - started > VDR_RAG_CATCHUP_MAX_AGE_MS) {
            return { ...item, status: 'failed', error: INDEXING_FAILED_MESSAGE };
        }
        return item;
    });

/** Strip ephemeral upload/index state before redux-persist writes localStorage. */
export const stripPersistedDocumentsEphemeral = inboundState => {
    if (!inboundState || typeof inboundState !== 'object') return inboundState;
    return {
        ...inboundState,
        indexingFiles: [],
        uploadTray: emptyUploadTray(),
    };
};
